import { AssetsLoader } from '@dealstack/frontend/shared/components/AssetsLoader/AssetsLoader';
import { DownloadInstructionButton } from '@dealstack/frontend/shared/components/fundament/Button/CommonButtons';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { PrimaryBall } from '@dealstack/frontend/shared/components/listOld/PrimaryListItem/PrimaryListItem';
import {
	TransInvestor,
	useTranslationInvestor,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import React, { memo, useRef, useState } from 'react';
import styled from 'styled-components';

interface HelpStep {
	title: string;
	stepSubList: { title: string; listItemSublist?: string[] }[];
	images?: boolean;
}

const getProvideDocsHelpSteps = (requestCertifiedCopiesOfDocuments = false): HelpStep[] => [
	{
		title: 'how_provide_docs.steps_list.step_1.title',
		stepSubList: [
			{
				title: 'how_provide_docs.steps_list.step_1.step_sub_list.item_1.title',
				listItemSublist: [
					'how_provide_docs.steps_list.step_1.step_sub_list.item_1.list_item_sub_list.item_1',
					'how_provide_docs.steps_list.step_1.step_sub_list.item_1.list_item_sub_list.item_2',
					'how_provide_docs.steps_list.step_1.step_sub_list.item_1.list_item_sub_list.item_3',
					'how_provide_docs.steps_list.step_1.step_sub_list.item_1.list_item_sub_list.item_4',
				],
			},
			{
				title: 'how_provide_docs.steps_list.step_1.step_sub_list.item_2.title',
				listItemSublist: [
					'how_provide_docs.steps_list.step_1.step_sub_list.item_2.list_item_sub_list.item_1',
					'how_provide_docs.steps_list.step_1.step_sub_list.item_2.list_item_sub_list.item_2',
					'how_provide_docs.steps_list.step_1.step_sub_list.item_2.list_item_sub_list.item_3',
					'how_provide_docs.steps_list.step_1.step_sub_list.item_2.list_item_sub_list.item_4',
				],
			},
			{
				title: 'how_provide_docs.steps_list.step_1.step_sub_list.item_3.title',
				listItemSublist: [
					'how_provide_docs.steps_list.step_1.step_sub_list.item_3.list_item_sub_list.item_1',
					'how_provide_docs.steps_list.step_1.step_sub_list.item_3.list_item_sub_list.item_2',
					'how_provide_docs.steps_list.step_1.step_sub_list.item_3.list_item_sub_list.item_3',
					'how_provide_docs.steps_list.step_1.step_sub_list.item_3.list_item_sub_list.item_4',
				],
			},
		],
	},
	{
		title: 'how_provide_docs.steps_list.step_2.title',
		stepSubList: [
			{
				title: 'how_provide_docs.steps_list.step_2.step_sub_list.item_1.title',
			},
			{
				title: 'how_provide_docs.steps_list.step_2.step_sub_list.item_2.title',
			},
		],
	},
	{
		title: 'how_provide_docs.steps_list.step_3.title',
		stepSubList: [
			{
				title: 'how_provide_docs.steps_list.step_3.step_sub_list.item_1.title',
			},
		],
		images: true,
	},
	...(() => {
		return requestCertifiedCopiesOfDocuments
			? [
					{
						title: 'how_provide_docs.steps_list.step_4.title',
						stepSubList: [
							{
								title: 'how_provide_docs.steps_list.step_4.step_sub_list.item_1.title',
							},
							{
								title: 'how_provide_docs.steps_list.step_4.step_sub_list.item_2.title',
								listItemSublist: [
									'how_provide_docs.steps_list.step_4.step_sub_list.item_2.list_item_sub_list.item_1',
									'how_provide_docs.steps_list.step_4.step_sub_list.item_2.list_item_sub_list.item_2',
									'how_provide_docs.steps_list.step_4.step_sub_list.item_2.list_item_sub_list.item_3',
									'how_provide_docs.steps_list.step_4.step_sub_list.item_2.list_item_sub_list.item_4',
									'how_provide_docs.steps_list.step_4.step_sub_list.item_2.list_item_sub_list.item_5',
								],
							},
						],
					},
					{
						title: 'how_provide_docs.steps_list.step_5.title',
						stepSubList: [
							{
								title: 'how_provide_docs.steps_list.step_5.step_sub_list.item_1.title',
							},
							{
								title: 'how_provide_docs.steps_list.step_5.step_sub_list.item_2.title',
							},
							{
								title: 'how_provide_docs.steps_list.step_5.step_sub_list.item_3.title',
							},
							{
								title: 'how_provide_docs.steps_list.step_5.step_sub_list.item_4.title',
							},
							{
								title: 'how_provide_docs.steps_list.step_5.step_sub_list.item_5.title',
								listItemSublist: [
									'how_provide_docs.steps_list.step_5.step_sub_list.item_5.list_item_sub_list.item_1',
									'how_provide_docs.steps_list.step_5.step_sub_list.item_5.list_item_sub_list.item_2',
									'how_provide_docs.steps_list.step_5.step_sub_list.item_5.list_item_sub_list.item_3',
									'how_provide_docs.steps_list.step_5.step_sub_list.item_5.list_item_sub_list.item_4',
								],
							},
						],
					},
					{
						title: 'how_provide_docs.steps_list.step_6.title',
						stepSubList: [
							{
								title: 'how_provide_docs.steps_list.step_6.step_sub_list.item_1.title',
							},
						],
					},
				]
			: [];
	})(),
];

interface IHowToProvideDocsProps {
	className?: string;
	requestCertifiedCopiesOfDocuments?: boolean;
}

const INVISIBLE_INSTRUCTION_CONTAINER_ID = 'invisible-instruction-container';
const InvisibleInstructionContainer = styled.div`
	position: absolute;
	width: 595.28px;
	height: 100vh;
	top: 0;
	left: 0;
	z-index: 1;
	overflow: scroll;
	opacity: 0;
	pointer-events: none;
`;

const HowToProvideDocs = ({
	className,
	requestCertifiedCopiesOfDocuments,
}: IHowToProvideDocsProps) => {
	const tInvestor = useTranslationInvestor();
	const selfRef = useRef<HTMLDivElement>(null);
	const [showInstructionTemplate, setShowInstructionTemplate] = useState(false);

	return (
		<div ref={selfRef} className={className}>
			{showInstructionTemplate ? (
				<InvisibleInstructionContainer id={INVISIBLE_INSTRUCTION_CONTAINER_ID}>
					<HowToProvideDocs />
				</InvisibleInstructionContainer>
			) : null}

			<Text variant="h4" bold>
				{tInvestor('how_provide_docs.title')}
			</Text>
			<DownloadInstructionButton
				data-html2canvas-ignore
				onClick={async () => {
					if (!selfRef?.current) {
						return;
					}
					const content = selfRef.current;
					setShowInstructionTemplate(true);
					// d.slaby MIP-421 16.02.2023
					// this is delay so react have a chance to render stuff
					setTimeout(async () => {
						if (!content) {
							return;
						}
						const { HTMLToPDF } = await import(
							'@dealstack/frontend/shared/other/react/htmlToPDF'
						);
						await HTMLToPDF({
							htmlElement: content,
							html2CanvasOptions: {
								height: content.offsetHeight,
							},
							downloadFileName: 'dealstack how to provide document instruction.pdf',
						});
						setShowInstructionTemplate(false);
					}, 20);
				}}
			/>
			<Subtitle>
				<Text variant="p-md">{tInvestor('how_provide_docs.subtitle')}</Text>
				<div>
					<Icon variant="badge" size="lg" />
					<Text bold>{tInvestor('how_provide_docs.proof_ident')}</Text>
				</div>
				<div>
					<Icon variant="list-home" size="lg" />
					<Text bold>{tInvestor('how_provide_docs.proof_address')}</Text>
				</div>
				<div>
					<Icon variant="buildings" size="lg" />
					<Text bold>{tInvestor('how_provide_docs.piv_documents')}</Text>
				</div>
			</Subtitle>

			<Text variant="p-lg" bold>
				{tInvestor('how_provide_docs.list_title')}
			</Text>

			<StepsList>
				{getProvideDocsHelpSteps(requestCertifiedCopiesOfDocuments).map((step, index) => {
					return (
						<SingleStep
							number={index + 1}
							title={tInvestor(step.title)}
							key={step.title}
						>
							<StepSubList>
								{step.stepSubList.map((stepSubListItem) => {
									return (
										<li key={stepSubListItem.title}>
											<Text variant="p-lg">
												<TransInvestor i18nKey={stepSubListItem.title} />
											</Text>
											{!!stepSubListItem.listItemSublist && (
												<ul>
													{stepSubListItem.listItemSublist.map((text) => {
														return (
															<li key={text}>
																<Text variant="p-lg">
																	{tInvestor(text)}
																</Text>
															</li>
														);
													})}
												</ul>
											)}
										</li>
									);
								})}

								{step.images && (
									<ImagesWrapper>
										<AssetsLoader src="/context/investor/how-to-upload-documents/example-document-pair.png" />
									</ImagesWrapper>
								)}
							</StepSubList>
						</SingleStep>
					);
				})}
			</StepsList>
		</div>
	);
};

const Subtitle = styled.div`
	margin-top: 1em;
	margin-bottom: 2em;

	& > div {
		display: flex;
		align-items: center;
		margin: 0.7em;
		gap: 0.5em;
	}
`;
const StepsList = styled.div`
	margin-top: 1em;
`;

interface SingleStepProps {
	className?: string;
	children: React.ReactNode;
	title: string;
	number: number;
}

const SingleStep = styled(({ className, children, title, number }: SingleStepProps) => {
	return (
		<div className={className}>
			<div>
				<PrimaryBall number={number} />
				<Text variant="h5" bold>
					{title}
				</Text>
			</div>
			<div>{children}</div>
		</div>
	);
})`
	& > div:first-of-type {
		display: flex;
		align-items: center;
		gap: 1em;
	}

	margin-bottom: 2em;
`;

const StepSubList = styled.ul`
	list-style: lower-alpha;
	margin-left: 4.1em;
	margin-top: 1em;

	ul {
		list-style: disc;
		margin: 0.5em 1em;
	}

	li {
		margin: 0.3em 0;
	}
`;

const ImagesWrapper = styled.div`
	width: 100%;

	img {
		width: 100%;
		height: auto;
	}
`;

const Memoized = memo(HowToProvideDocs);
const Styled = styled(Memoized)`
	strong {
		font-weight: bold;
	}

	${DownloadInstructionButton} {
		margin-top: 1em;
	}

	i {
		font-style: italic;
	}

	${Text} {
		display: inline;
	}
`;

export { Styled as HowToProvideDocs };
